import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { SimpleSelect } from "./simple-select"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 rounded-lg shadow-lg", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-0 sm:space-x-4 sm:space-y-0",
        month: "space-y-2",
        month_caption: cn(
          "flex justify-center pt-1 items-center mb-2",
          props.captionLayout === "dropdown" ? "w-full" : "relative"
        ),
        caption_label:
          props.captionLayout === "dropdown"
            ? "hidden"
            : "text-base font-medium",
        nav: cn(
          "space-x-1 flex items-center",
          props.captionLayout === "dropdown" && "hidden"
        ),
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-muted transition-colors absolute left-1"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-muted transition-colors absolute right-1"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-10 font-medium text-[0.9rem]",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent/20 [&:has([aria-selected].outside)]:bg-accent/10 [&:has([aria-selected].range_end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.range_end)]:rounded-r-md [&:has(>.range_start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day_button: cn(
          buttonVariants({ variant: "link" }),
          "h-10 w-10 p-0 font-normal aria-selected:opacity-100 transition-colors"
        ),
        range_start: "range-start",
        range_end: "range-end",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground font-medium",
        today: "bg-accent/15 font-medium border border-accent/30",
        outside:
          "outside text-muted-foreground aria-selected:bg-accent/10 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent/20 aria-selected:text-accent-foreground",
        hidden: "invisible",
        dropdowns: "flex flex-row gap-2 flex-1",
        dropdown_root: "flex-1",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className, ...props }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          return (
            <Icon className={cn("h-5 w-5", className)} {...(props as React.SVGProps<SVGSVGElement>)} />
          )
        },
        Dropdown: ({ className, ...props }) => {
          const { onChange, options, value } = props;

          const simpleSelectOptions = options?.map((option) => ({
            value: option.value.toString(),
            label: option.label,
          })) || [];

          return (
            <SimpleSelect
              options={simpleSelectOptions}
              value={value?.toString() || ""}
              triggerClassName="h-8 px-2 text-sm"
              onValueChange={(newValue) => {
                if (onChange) {
                  const event = {
                    target: { value: newValue }
                  } as React.ChangeEvent<HTMLSelectElement>;
                  onChange(event);
                }
              }}
            />
          );
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
